/**
 * EVENTSFLO-ADMIN-BACKUP-UI-COMPONENT-SPLIT-V1
 *
 * SuperAdmin Backups page — orchestrator. Owns top-level data (status,
 * list, pagination, dialog/drawer open targets) and composes:
 *   - BackupStatusCard
 *   - BackupArchiveTable
 *   - BackupCreateDialog (self-contained 2-step typed-EXPORT flow)
 *   - BackupDeleteDialog (self-contained typed-DELETE flow)
 *   - BackupInspectDrawer (6-tab drawer; includes restore safety tab)
 *
 * Wires the 16 admin-backup endpoints (UI-only — no backend changes).
 * NOT IMPLEMENTED by design:
 *   - Live restore / import
 *   - Offsite backup
 *   - Reminder automation
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Database, Play, RefreshCw } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { PAGE_SIZE } from "./backupFormatters";
import {
  getStatus,
  listBackups,
  downloadBackup,
} from "./backupApi";
import BackupStatusCard from "./BackupStatusCard";
import BackupArchiveTable from "./BackupArchiveTable";
import BackupCreateDialog from "./BackupCreateDialog";
import BackupDeleteDialog from "./BackupDeleteDialog";
import BackupInspectDrawer from "./BackupInspectDrawer";
// EVENTSFLO-ADMIN-BACKUP-RESTORE-RUNS-HISTORY-V1
import BackupRestoreRunsSection from "./BackupRestoreRunsSection";
// EVENTSFLO-ADMIN-BACKUP-REMINDER-SETTINGS-V1
import BackupReminderSettingsCard from "./BackupReminderSettingsCard";
// EVENTSFLO-ADMIN-BACKUP-OFFSITE-V1
import BackupOffsiteCard from "./BackupOffsiteCard";
// EVENTSFLO-ADMIN-BACKUP-OFFSITE-COPIES-HISTORY-V1
import BackupOffsiteCopiesHistory from "./BackupOffsiteCopiesHistory";
// EVENTSFLO-DOCUMENT-AUDIT-RETENTION-STATUS-WIDGET-V1 — tiny
// SuperAdmin-only visibility card. Read-only; no purge button.
import AuditRetentionStatusCard from "./AuditRetentionStatusCard";
// EVENTSFLO-LIVE-RESTORE-PHASE-5-ADMIN-UI-V1 — SuperAdmin wizard
// to drive the Phase 5 metadata-only cutover backend.
import LiveRestoreCutoverCard from "./LiveRestoreCutoverCard";
// EVENTSFLO-GOOGLE-ROUTES-ADMIN-STATUS-V1 — SuperAdmin Google
// Routes visibility + safe test-lookup widget.
import GoogleRoutesStatusCard from "./GoogleRoutesStatusCard";
// EVENTSFLO-I18N-PHASE-2E-MACHINE-ASSISTED-ADMIN-WORKFLOW-V1 —
// SuperAdmin-only admin translation drafts card. Drafts only;
// English production UI unchanged.
import I18nDraftsCard from "./I18nDraftsCard";
// EVENTSFLO-I18N-PHASE-2F-DYNAMIC-CONTENT-CACHE-V1 —
// SuperAdmin-only approved-draft catalog/cache preparation card.
// production_activation is always false.
import I18nCatalogCacheCard from "./I18nCatalogCacheCard";
// EVENTSFLO-I18N-PHASE-2G-REVIEWED-PDF-EMAIL-TEMPLATES-V1 —
// SuperAdmin-only PDF/email template translation preparation.
// Live PDFs and emails remain English.
import I18nTemplatePreparationCard from "./I18nTemplatePreparationCard";
// EVENTSFLO-I18N-PHASE-2H-ADMIN-PORTAL-RUNTIME-LOOKUP-V1 —
// SuperAdmin-only runtime translation lookup preview. English
// remains the live default; production_activation is always false.
import I18nRuntimePreviewCard from "./I18nRuntimePreviewCard";
// EVENTSFLO-TASK-DEFINITIONS-PHASE-2.2-DELETE-MERGE-CLEANUP-V1 —
// SuperAdmin-only Task Definition cleanup tooling (scan,
// archive/delete, merge). Historical snapshots preserved.
import TaskDefinitionsCleanupCard from "./TaskDefinitionsCleanupCard";
// EVENTSFLO-CLIENT-PORTAL-PHASE-2.3-INVOICE-PAYMENT-FLOWS-V1 —
// SuperAdmin Client Payments admin card. Mock provider only;
// live charges and emails disabled; invoice totals untouched.
import ClientPaymentsAdminCard from "./ClientPaymentsAdminCard";
import SecurityBaselineCard from "./SecurityBaselineCard";
// EVENTSFLO-INBOX-INVOICE-IMPORT-AUDIT-PANEL-V1 — read-only Last 20 imports
import InboxInvoiceImportAuditCard from "./InboxInvoiceImportAuditCard";
// EVENTSFLO-PRE-DEPLOY-TEST-DATA-CLEANUP-AND-AUDIT-V1
import PreDeployCleanupCard from "./PreDeployCleanupCard";
// EVENTSFLO-PRE-DEPLOY-CLEANUP-AUDIT-PANEL-V1
import PreDeployCleanupHistoryCard from "./PreDeployCleanupHistoryCard";
// EVENTSFLO-WEB-PUSH-NOTIFICATIONS-PHASE-1-V1
import PushNotificationsSettingsCard from "./PushNotificationsSettingsCard";
// EVENTSFLO-CREW-ONBOARDING-EMAILS-PDFS-MESSAGES-I18N-V1
import CrewOnboardingCopyAuditCard from "./CrewOnboardingCopyAuditCard";
// EVENTSFLO-UI-HELP-BUTTON-STANDARD-V1
import PageHelpButton from "../../../components/common/PageHelpButton";

// Help content for /admin/backups. Kept here so it lives next to the
// page it documents and stays in sync with the visible UI.
const BACKUPS_HELP_SECTIONS = [
  {
    heading: "Page purpose",
    bullets: [
      "SuperAdmin-only backup administration and operational safety tools for the EventsFlo platform.",
      "Use this page for manual backup creation, archive inspection, isolated dry-run restores and offsite copy management.",
    ],
  },
  {
    heading: "Manual export, download, list & delete",
    bullets: [
      "Create a new on-demand archive of MongoDB collections via the typed-EXPORT flow on the Create backup dialog.",
      "Browse all archives in the table below, including expired ones, with pagination.",
      "Download an archive locally with the Download action.",
      "Delete an archive permanently via the typed-DELETE confirmation flow.",
    ],
  },
  {
    heading: "Archive inspection & Copy JSON",
    bullets: [
      "Open the Inspect drawer on any archive to view metadata, manifest, collection counts and integrity hashes.",
      "Use the Copy JSON buttons on each JSON block to copy the raw payload to the clipboard for offline review.",
    ],
  },
  {
    heading: "Restore preflight & isolated dry-run",
    bullets: [
      "Run a restore preflight on an archive to validate manifest, hash and target compatibility before any restore.",
      "Trigger an isolated dry-run restore which writes only to the configured isolated/sandbox database — never to live data.",
      "Use this to verify archive integrity and rehearse a restore safely.",
    ],
  },
  {
    heading: "Restore runs history",
    bullets: [
      "Read-only audit log of every isolated restore run, including who started it, the archive used, target DB and outcome.",
      "Use this as the source of truth for past restore activity and to correlate with audit logs.",
    ],
  },
  {
    heading: "Reminder settings",
    bullets: [
      "Configure cadence, recipient list and quiet hours for backup reminder notifications.",
      "Settings are persisted but reminders themselves are operator-driven in the current build (see Limitations).",
    ],
  },
  {
    heading: "Manual offsite copy",
    bullets: [
      "Copy an existing archive to the configured offsite destination on demand.",
      "Each copy is recorded as a single offsite copy event with status, destination and bytes copied.",
    ],
  },
  {
    heading: "Offsite copies history",
    bullets: [
      "Read-only history of every manual offsite copy event, with destination, status and outcome.",
      "Use this to verify which archives have been replicated offsite.",
    ],
  },
  {
    heading: "Live Restore Cutover (Phase 5)",
    bullets: [
      "Preflight checks the validated isolated restore against the current live DB and surfaces blockers, warnings and a collection-count comparison. Mutation-free.",
      "Apply is metadata-only: it records a rollback snapshot, writes an audit row tagged live_restore_cutover, and returns a manual SRE runbook. It does NOT swap the live database.",
      "Apply requires the exact confirmation phrase PROMOTE ISOLATED RESTORE TO LIVE.",
      "pending_manual status means the cutover intent is recorded; the operator must execute the runbook (env-var swap + supervisor restart) to finish the cutover.",
      "Rollback snapshot captures the previous live DB name and per-collection counts so the cutover can be reversed by the SRE.",
      "This control is restricted to SuperAdmins and intended for disaster-recovery only.",
    ],
  },
  {
    heading: "Google Routes status",
    bullets: [
      "Google Routes is opt-in and disabled by default. Disabled state means the legacy Distance Matrix / manual travel path remains active.",
      "Status surfaces enabled/disabled, API key configured/missing, key source (without revealing the key value), mode/units/region and cache TTL.",
      "Cache counts (total / valid / expired) and last cache write timestamp give operator visibility into the Mongo route_distance_cache collection.",
      "Test route lookup is on-demand only — no background polling. When the flag is off it short-circuits without calling Google.",
      "Enable guidance panel: enabling Google Routes requires an env-var change (`EVENTSFLO_GOOGLE_ROUTES_ENABLED=true`) plus a backend restart — this page does NOT enable it automatically. The runbook is copyable behind a confirmation phrase; the API key is never displayed.",
      "Post-enable health check validates the live provider after the operator finishes the manual env-swap + restart. It does NOT recalculate any project, quote, invoice or Travel TaskDefinition.",
    ],
  },
];

const BACKUPS_HELP_WARNINGS = [
  "Automated live restore / cutover / swap / promote is NOT available — Phase 5 prepares a manual SRE runbook only.",
  "Automated backup scheduling is NOT available — every archive is created manually.",
  "Automated reminder email dispatch is NOT available — reminder settings are persisted but not auto-sent.",
  "Offsite copy is manual only — there is no scheduler or automated replication.",
  "Isolated restore writes only to the configured isolated/sandbox database, never to the live tenant data.",
  "This page is SuperAdmin-only — tenant admins and other roles are denied access.",
];

export default function BackupsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === "superadmin";

  // Access guard — non-superadmins are bounced to /dashboard.
  useEffect(() => {
    if (user && !isSuperAdmin) {
      toast.error("Access denied. Superadmin privileges required.");
      navigate("/dashboard", { replace: true });
    }
  }, [user, isSuperAdmin, navigate]);

  // ── status / list state ─────────────────────────────────────────
  const [statusData, setStatusData] = useState(null);
  const [listData, setListData] = useState({ items: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingList, setLoadingList] = useState(true);

  // ── dialog / drawer open targets ────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [drawerBackup, setDrawerBackup] = useState(null);

  // ── loaders ─────────────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const { data } = await getStatus();
      setStatusData(data);
    } catch (e) {
      console.error("status fetch failed", e);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const fetchList = useCallback(async (newOffset) => {
    try {
      setLoadingList(true);
      const { data } = await listBackups({
        limit: PAGE_SIZE,
        offset: newOffset,
        include_expired: true,
        sort: "generated_at_desc",
      });
      setListData(data);
    } catch (e) {
      console.error("list fetch failed", e);
      toast.error("Failed to load backup list");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchStatus();
      fetchList(0);
    }
  }, [isSuperAdmin, fetchStatus, fetchList]);

  // ── action handlers ─────────────────────────────────────────────
  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
    fetchList(newOffset);
  };

  const handleRefresh = () => fetchList(offset);

  const handleCreated = () => {
    setOffset(0);
    fetchList(0);
    fetchStatus();
  };

  const handleDeleted = () => {
    setDeleteTarget(null);
    fetchList(offset);
    fetchStatus();
  };

  const handleDownload = async (b) => {
    try {
      const res = await downloadBackup(b.backup_id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      toast.error("Download failed");
      console.error(e);
    }
  };

  if (!isSuperAdmin) return null;

  return (
    <div className="p-6 space-y-6" data-testid="backups-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="w-6 h-6 text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Backups</h1>
            <p className="text-sm text-zinc-400">
              SuperAdmin-only manual backup, inspection and restore-safety tooling.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PageHelpButton
            testId="admin-backups"
            title="Admin Backups — Help"
            description="SuperAdmin-only backup administration and operational safety tools."
            sections={BACKUPS_HELP_SECTIONS}
            warnings={BACKUPS_HELP_WARNINGS}
          />
          <Button variant="outline" size="sm" onClick={handleRefresh} data-testid="refresh-list-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="open-create-backup-btn">
            <Play className="w-4 h-4 mr-1" /> Create backup
          </Button>
        </div>
      </div>

      <BackupStatusCard status={statusData} loading={loadingStatus} />

      {/* EVENTSFLO-ADMIN-BACKUP-REMINDER-SETTINGS-V1 — read/update reminder configuration */}
      <BackupReminderSettingsCard />

      {/* EVENTSFLO-ADMIN-BACKUP-OFFSITE-V1 — manual offsite copy of an existing archive */}
      <BackupOffsiteCard />

      {/* EVENTSFLO-ADMIN-BACKUP-OFFSITE-COPIES-HISTORY-V1 — read-only history */}
      <BackupOffsiteCopiesHistory />

      {/* EVENTSFLO-DOCUMENT-AUDIT-RETENTION-STATUS-WIDGET-V1 — read-only retention visibility */}
      <AuditRetentionStatusCard />

      {/* EVENTSFLO-LIVE-RESTORE-PHASE-5-ADMIN-UI-V1 — SuperAdmin cutover wizard */}
      <LiveRestoreCutoverCard />

      {/* EVENTSFLO-GOOGLE-ROUTES-ADMIN-STATUS-V1 — SuperAdmin Routes status + test lookup */}
      <GoogleRoutesStatusCard />

      {/* EVENTSFLO-I18N-PHASE-2E-MACHINE-ASSISTED-ADMIN-WORKFLOW-V1 — SuperAdmin admin i18n drafts */}
      <I18nDraftsCard />

      {/* EVENTSFLO-I18N-PHASE-2F-DYNAMIC-CONTENT-CACHE-V1 — SuperAdmin approved-draft catalog cache */}
      <I18nCatalogCacheCard />

      {/* EVENTSFLO-I18N-PHASE-2G-REVIEWED-PDF-EMAIL-TEMPLATES-V1 — SuperAdmin PDF/email template preparation */}
      <I18nTemplatePreparationCard />

      {/* EVENTSFLO-I18N-PHASE-2H-ADMIN-PORTAL-RUNTIME-LOOKUP-V1 — SuperAdmin runtime lookup preview */}
      <I18nRuntimePreviewCard />

      {/* EVENTSFLO-TASK-DEFINITIONS-PHASE-2.2-DELETE-MERGE-CLEANUP-V1 — SuperAdmin Task Definitions cleanup */}
      <TaskDefinitionsCleanupCard />

      {/* EVENTSFLO-CLIENT-PORTAL-PHASE-2.3-INVOICE-PAYMENT-FLOWS-V1 — SuperAdmin Client Payments admin */}
      <ClientPaymentsAdminCard />

      {/* EVENTSFLO-INBOX-INVOICE-IMPORT-AUDIT-PANEL-V1 — Last 20 Inbox invoice imports (read-only) */}
      <InboxInvoiceImportAuditCard />

      {/* EVENTSFLO-PRE-DEPLOY-TEST-DATA-CLEANUP-AND-AUDIT-V1 — dry-run-first TEST data cleanup */}
      <PreDeployCleanupCard />

      {/* EVENTSFLO-PRE-DEPLOY-CLEANUP-AUDIT-PANEL-V1 — Last 10 cleanup runs (read-only) */}
      <PreDeployCleanupHistoryCard />

      {/* EVENTSFLO-WEB-PUSH-NOTIFICATIONS-PHASE-1-V1 — foundation only, no live sends */}
      <PushNotificationsSettingsCard />

      {/* EVENTSFLO-CREW-ONBOARDING-EMAILS-PDFS-MESSAGES-I18N-V1 — read-only copy/i18n catalog + dry-run preview */}
      <CrewOnboardingCopyAuditCard />

      {/* EVENTSFLO-SECURITY-HEADERS-AND-BASELINE-POSTURE-REVIEW-V1 — SuperAdmin security baseline */}
      <SecurityBaselineCard />

      <BackupArchiveTable
        items={listData.items || []}
        total={listData.total || 0}
        offset={offset}
        loading={loadingList}
        onPageChange={handlePageChange}
        onInspect={setDrawerBackup}
        onDownload={handleDownload}
        onDelete={setDeleteTarget}
      />

      <BackupCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
      />

      <BackupDeleteDialog
        target={deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />

      <BackupInspectDrawer
        backup={drawerBackup}
        onOpenChange={(o) => !o && setDrawerBackup(null)}
      />

      {/* EVENTSFLO-ADMIN-BACKUP-RESTORE-RUNS-HISTORY-V1 — read-only history of isolated restore runs */}
      <BackupRestoreRunsSection />
    </div>
  );
}
